import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { tokenStore, xano, type User } from "@/lib/xano";

type AuthState = {
  user: User | null;
  status: "loading" | "authed" | "anon";
};

type SignupArgs = {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  source_site_slug?: string;
};

type AuthContextValue = AuthState & {
  login: (email: string, password: string) => Promise<void>;
  signup: (args: SignupArgs) => Promise<void>;
  logout: () => void;
  refresh: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ user: null, status: "loading" });
  const qc = useQueryClient();

  const refresh = useCallback(async () => {
    if (!tokenStore.get()) {
      setState({ user: null, status: "anon" });
      return;
    }
    try {
      const user = await xano.auth.me();
      setState({ user, status: "authed" });
    } catch {
      tokenStore.clear();
      setState({ user: null, status: "anon" });
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(
    async (email: string, password: string) => {
      const res = await xano.auth.login(email, password);
      tokenStore.set(res.authToken);
      await refresh();
      qc.invalidateQueries();
    },
    [qc, refresh]
  );

  const signup = useCallback(
    async (args: SignupArgs) => {
      const res = await xano.auth.signup(args);
      tokenStore.set(res.authToken);
      if (res.user) setState({ user: res.user, status: "authed" });
      else await refresh();
      qc.invalidateQueries();
    },
    [qc, refresh]
  );

  const logout = useCallback(() => {
    tokenStore.clear();
    setState({ user: null, status: "anon" });
    qc.clear();
  }, [qc]);

  const value = useMemo(
    () => ({ ...state, login, signup, logout, refresh }),
    [state, login, signup, logout, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
